import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound, Mail, Lock, Phone, User, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";

const Field = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
    <input
      {...props}
      className="w-full rounded-full border border-ink/15 bg-white py-3.5 pl-11 pr-5 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
    />
  </div>
);

export default function SignIn() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user =
        mode === "login"
          ? await login(form.email, form.password)
          : await register(form);
      toast.success(`Welcome${user.name ? `, ${user.name.split(" ")[0]}` : ""}!`);
      navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section data-testid="signin-page" className="pt-32 pb-28 lg:pt-40">
      <div className="mx-auto max-w-md px-4 sm:px-6">
        <Reveal y={14}>
          <p className="flex items-center justify-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
            <span className="h-px w-8 bg-bark" aria-hidden="true" />
            Customer portal
            <span className="h-px w-8 bg-bark" aria-hidden="true" />
          </p>
          <h1 className="mt-5 text-center font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
            {mode === "login" ? (
              <>
                Welcome <em className="font-editorial italic text-bark">back.</em>
              </>
            ) : (
              <>
                Join the <em className="font-editorial italic text-bark">pack.</em>
              </>
            )}
          </h1>
          <p className="mt-4 text-center text-sm leading-relaxed text-slate-600">
            Sign in to create your pup's profile, book services and manage stays.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 rounded-3xl border border-ink/8 bg-white p-7 shadow-[0_24px_60px_-35px_rgba(15,23,42,0.35)] sm:p-8">
            <div className="grid grid-cols-2 rounded-full bg-cream-dark p-1 text-sm font-semibold">
              <button
                type="button"
                data-testid="signin-tab-login"
                onClick={() => setMode("login")}
                className={`rounded-full py-2.5 transition-colors duration-200 ${
                  mode === "login" ? "bg-navy text-cream" : "text-navy/60"
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                data-testid="signin-tab-register"
                onClick={() => setMode("register")}
                className={`rounded-full py-2.5 transition-colors duration-200 ${
                  mode === "register" ? "bg-navy text-cream" : "text-navy/60"
                }`}
              >
                Create account
              </button>
            </div>

            <form data-testid="signin-form" onSubmit={submit} className="mt-6 space-y-4">
              {mode === "register" && (
                <>
                  <Field icon={User} data-testid="register-name" type="text" required placeholder="Full name" value={form.name} onChange={set("name")} />
                  <Field icon={Phone} data-testid="register-phone" type="tel" placeholder="Phone (for booking updates)" value={form.phone} onChange={set("phone")} />
                </>
              )}
              <Field icon={Mail} data-testid="signin-email" type="email" required placeholder="Email address" value={form.email} onChange={set("email")} />
              <Field icon={Lock} data-testid="signin-password" type="password" required minLength={6} placeholder="Password" value={form.password} onChange={set("password")} />
              <button
                type="submit"
                data-testid="signin-submit"
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-bark px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
              >
                <LogIn className="h-4 w-4" aria-hidden="true" />
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create my account"}
              </button>
            </form>

            <div className="my-6 flex items-center gap-4 text-xs text-slate-400">
              <span className="h-px flex-1 bg-ink/10" />
              or
              <span className="h-px flex-1 bg-ink/10" />
            </div>

            <button
              type="button"
              data-testid="signin-google-button"
              onClick={googleLogin}
              className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-ink/15 bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:border-navy/40"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/>
              </svg>
              Continue with Google
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-slate-500">
            <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
            One account for all your pups — bookings, records and updates.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
